import SEO from '@/components/SEO';
import BrandMarquee from '@/components/BrandMarquee';
import { site } from '@/data/content';
import {
  Share2,
  ArrowUpRight,
  Check,
  MessageSquare,
  Users,
  TrendingUp,
  Calendar,
  Zap,
  Bell,
  Target,
  Shield,
  ChevronDown,
  ChevronUp,
  PhoneCall,
} from 'lucide-react';
import { useState } from 'react';

const features = [
  { icon: MessageSquare, title: 'Unified social inbox', text: 'Manage messages, comments, and mentions from Facebook, LinkedIn, Instagram, X, YouTube, and TikTok in one unified inbox.' },
  { icon: Users, title: 'Team collaboration', text: 'Assign conversations, add internal notes, and collaborate with your team across all social platforms.' },
  { icon: TrendingUp, title: 'Engagement analytics', text: 'Track engagement rates, follower growth, and performance metrics across all platforms in one dashboard.' },
  { icon: Calendar, title: 'Content scheduling', text: 'Plan and schedule posts across all platforms with a unified content calendar and queue management.' },
];

const useCases = [
  { title: 'Customer support', text: 'Handle customer inquiries and support requests across all social platforms from one inbox.' },
  { title: 'Marketing campaigns', text: 'Plan, schedule, and execute marketing campaigns across multiple platforms simultaneously.' },
  { title: 'Brand monitoring', text: 'Monitor brand mentions, track sentiment, and respond to conversations about your brand.' },
  { title: 'Community management', text: 'Build and engage your community with consistent posting and timely responses.' },
];

const benefits = [
  { icon: Zap, title: 'Save time with automation', description: 'Automate responses, schedule content, and streamline workflows to focus on high-value interactions.' },
  { icon: Bell, title: 'Never miss a message', description: 'Real-time notifications and smart routing ensure every message gets attention from the right team member.' },
  { icon: Target, title: 'Audience insights', description: 'Understand your audience with detailed analytics on demographics, behavior, and engagement patterns.' },
  { icon: Shield, title: 'Brand protection', description: 'Monitor brand mentions, moderate comments, and maintain consistent brand voice across all platforms.' },
];

const faqs: Array<{ question: string; answer: string }> = [
  {
    question: 'What is Social Media Management?',
    answer: 'Social Media Management is an Enterprise feature that lets you manage Facebook, LinkedIn, Instagram, X, YouTube, and TikTok from one unified workspace with a single inbox, scheduling tools, and analytics.',
  },
  {
    question: 'Can I schedule posts in advance?',
    answer: 'Yes. You can schedule posts across all platforms with our unified content calendar. Set dates, times, and queue posts for automatic publishing.',
  },
  {
    question: 'How does the unified inbox work?',
    answer: 'All messages, comments, and mentions from connected platforms appear in one inbox. You can filter by platform, assign to team members, and respond without switching tools.',
  },
  {
    question: 'Is Social Media Management included in Enterprise?',
    answer: 'Yes. Social Media Management is an exclusive feature included in the Enterprise plan at no additional cost.',
  },
  {
    question: 'Can multiple team members collaborate?',
    answer: 'Yes. Team members can collaborate with role-based permissions, assignment workflows, internal notes, and real-time notifications.',
  },
];

function FAQAccordion({ faqs }: { faqs: Array<{ question: string; answer: string }> }) {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  return (
    <div className="space-y-3">
      {faqs.map((faq, index) => (
        <div
          key={index}
          className="rounded-2xl border border-slate-200 bg-white shadow-sm"
        >
          <button
            onClick={() => setOpenIndex(openIndex === index ? null : index)}
            className="flex w-full items-center justify-between p-5 text-left"
            aria-expanded={openIndex === index}
          >
            <span className="pr-4 font-semibold text-slate-900">{faq.question}</span>
            {openIndex === index ? (
              <ChevronUp className="h-5 w-5 shrink-0 text-slate-500" />
            ) : (
              <ChevronDown className="h-5 w-5 shrink-0 text-slate-500" />
            )}
          </button>
          {openIndex === index && (
            <div className="px-5 pb-5">
              <p className="text-slate-600">{faq.answer}</p>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

export default function SocialMediaManagement() {
  return (
    <>
      <SEO
        title="Social Media Management - Enterprise Multi-Platform Management | ZuriHub"
        description="Manage Facebook, LinkedIn, Instagram, X, YouTube, and TikTok from one unified inbox with Enterprise Social Media Management. Scheduling, analytics, and team collaboration included."
        path="/social-media-management"
      />
      <section className="relative overflow-hidden bg-slate-950 px-4 pt-32 pb-32 text-white sm:px-6 lg:px-8">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-brand-900/40 via-slate-950 to-slate-950" />
        <div className="relative mx-auto max-w-6xl">
          <div className="grid gap-12 lg:grid-cols-2 lg:gap-16">
            <div>
              <span className="inline-flex items-center gap-2 rounded-full border border-brand-500/30 bg-brand-500/10 px-4 py-1.5 text-sm font-semibold text-brand-300 backdrop-blur">
                <Share2 className="h-4 w-4" /> Enterprise Only
              </span>
              <h1 className="mt-6 text-4xl font-extrabold tracking-tight sm:text-5xl lg:text-6xl">
                All your social media in <span className="gradient-text">one unified inbox</span>
              </h1>
              <p className="mt-6 text-lg text-slate-300">
                Manage Facebook, LinkedIn, Instagram, X, YouTube, and TikTok from one unified workspace. Schedule content, collaborate with your team, and track performance across all platforms with Enterprise-grade tools.
              </p>
              <div className="mt-8 flex flex-col gap-4 sm:flex-row">
                <a
                  href={site.workspaceUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 rounded-full bg-white px-8 py-4 text-base font-bold text-slate-900 transition hover:bg-slate-100 shadow-xl shadow-white/10"
                >
                  Start Free Trial <ArrowUpRight className="h-4 w-4" />
                </a>
                <a
                  href="/book-a-demo"
                  className="inline-flex items-center justify-center gap-2 rounded-full border border-slate-600 bg-slate-900/50 px-8 py-4 text-base font-semibold text-white backdrop-blur transition hover:border-brand-500 hover:bg-slate-800"
                >
                  Book a demo <Share2 className="h-4 w-4" />
                </a>
              </div>
            </div>
            <div className="relative hidden lg:block">
              <div className="absolute inset-0 bg-gradient-to-br from-brand-600/20 to-brand-400/10 blur-3xl" />
              <div className="relative">
                <img
                  src="https://media.getzurihub.com/Jayrah%20Assets/AZ-%20SOCIAL.png"
                  alt="Social Media Management dashboard showing unified inbox and multi-platform management"
                  className="rounded-3xl shadow-2xl shadow-brand-900/30"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      <BrandMarquee compact />

      <section className="py-24" aria-labelledby="features-heading">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mb-14 text-center">
            <h2 id="features-heading" className="section-title">Powerful social media features</h2>
            <p className="section-subtitle mx-auto mt-4">Everything you need for enterprise social media management.</p>
          </div>
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {features.map((f) => (
              <div key={f.title} className="rounded-3xl border border-slate-100 bg-white p-8 shadow-sm transition hover:-translate-y-1 hover:shadow-lg">
                <div className="mb-5 flex h-14 w-14 items-center justify-center rounded-2xl bg-brand-50 text-brand-600">
                  <f.icon className="h-7 w-7" />
                </div>
                <h3 className="text-lg font-bold text-slate-900">{f.title}</h3>
                <p className="mt-2 text-sm text-slate-600">{f.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-slate-50 py-24" aria-labelledby="benefits-heading">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mb-14 text-center">
            <h2 id="benefits-heading" className="section-title">Why Enterprise needs Social Media Management</h2>
            <p className="section-subtitle mx-auto mt-4">Benefits that transform social media operations.</p>
          </div>
          <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-4">
            {benefits.map((benefit) => (
              <div key={benefit.title} className="rounded-3xl border border-slate-100 bg-white p-8 shadow-sm">
                <div className="mb-5 flex h-14 w-14 items-center justify-center rounded-2xl bg-brand-50 text-brand-600">
                  <benefit.icon className="h-7 w-7" />
                </div>
                <h3 className="text-lg font-bold text-slate-900">{benefit.title}</h3>
                <p className="mt-2 text-sm text-slate-600">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24" aria-labelledby="usecases-heading">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <h2 id="usecases-heading" className="text-center section-title">Use cases</h2>
          <div className="mt-10 grid gap-6 sm:grid-cols-2">
            {useCases.map((u) => (
              <div key={u.title} className="rounded-2xl border border-slate-100 bg-white p-6 shadow-sm">
                <h3 className="font-bold text-slate-900">{u.title}</h3>
                <p className="mt-2 text-sm text-slate-600">{u.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <BrandMarquee />

      <section className="bg-slate-900 py-24 text-white" aria-labelledby="how-heading">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
          <div className="grid gap-12 lg:grid-cols-2">
            <div>
              <h2 id="how-heading" className="text-3xl font-extrabold tracking-tight sm:text-4xl">How it works</h2>
              <p className="mt-4 text-slate-300">
                From message to publication, Social Media Management streamlines every step of your social media operations.
              </p>
              <div className="mt-8 space-y-4">
                {[
                  {
                    icon: MessageSquare,
                    title: 'Unified inbox',
                    description: 'All messages, comments, and mentions from connected platforms appear in one inbox.',
                  },
                  {
                    icon: Users,
                    title: 'Team collaboration',
                    description: 'Assign to team members, add notes, and collaborate on responses in real-time.',
                  },
                  {
                    icon: PhoneCall,
                    title: 'Content scheduling',
                    description: 'Plan and schedule posts across all platforms with a unified content calendar.',
                  },
                ].map((item) => (
                  <div key={item.title} className="flex gap-4">
                    <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-brand-100 text-brand-600">
                      <item.icon className="h-6 w-6" />
                    </div>
                    <div>
                      <h3 className="font-bold">{item.title}</h3>
                      <p className="mt-1 text-sm text-slate-400">{item.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="rounded-3xl border border-slate-700 bg-slate-800/50 p-8">
              <h3 className="font-bold">Social Media Management features</h3>
              <ul className="mt-4 space-y-3 text-slate-300">
                <li className="flex items-start gap-2"><Check className="mt-0.5 h-4 w-4 text-green-400" /> Unified inbox for all platforms</li>
                <li className="flex items-start gap-2"><Check className="mt-0.5 h-4 w-4 text-green-400" /> Team collaboration with assignments</li>
                <li className="flex items-start gap-2"><Check className="mt-0.5 h-4 w-4 text-green-400" /> Content scheduling and calendar</li>
                <li className="flex items-start gap-2"><Check className="mt-0.5 h-4 w-4 text-green-400" /> Performance analytics and reporting</li>
                <li className="flex items-start gap-2"><Check className="mt-0.5 h-4 w-4 text-green-400" /> Multi-platform management</li>
                <li className="flex items-start gap-2"><Check className="mt-0.5 h-4 w-4 text-green-400" /> Real-time notifications</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-slate-50 py-24" aria-labelledby="faq-heading">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
          <div className="mb-14 text-center">
            <h2 id="faq-heading" className="section-title">Frequently asked questions</h2>
            <p className="section-subtitle mx-auto mt-4">Everything you need to know about Social Media Management</p>
          </div>
          <FAQAccordion faqs={faqs} />
        </div>
      </section>
    </>
  );
}
